import {
  STEP_GROUPS,
  STEP_GATE_NAMES,
  STEP_NAMES,
} from 'accounts/constants/steps';
import type { FlowStepModel } from 'api/models';
import { SetupDataTypes, STATUS, StepStatus } from 'models/enums';
import { HomeownerSetupStepNames } from 'models/enums/index';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import HomeownerAccountUnit from 'models/homeownerAccounts/HomeownerAccountUnit';
import { BASE_PATHS } from 'routes/paths';

function getStepGroupByName(name: any) {
  return STEP_GROUPS.find((group) => group.name === name);
}

const STEPS_CONFIGURATION = [
  // InspectionSchedule
  {
    name: STEP_NAMES.UnitOccupancy,
    displayName: 'Occupancy',
    key: 'occupancy',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.PropertyFeatures,
    displayName: 'Property Features',
    key: 'features',
    group: getStepGroupByName(SetupDataTypes.Property),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.UnitFeatures,
    displayName: 'Home Features',
    key: 'features',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.PropertyHoa,
    displayName: 'HOA',
    key: 'hoa',
    group: getStepGroupByName(SetupDataTypes.Property),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.UnitInspectionPreferences,
    displayName: 'Schedule',
    key: 'inspection',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.UnitKeysEnhanced,
    displayName: 'Keys',
    key: 'keys-enhanced',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.UnitInspectionPrep,
    displayName: 'Walkthrough Prep',
    key: 'inspection-prep',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },
  {
    name: STEP_NAMES.UnitInspectionSchedule,
    displayName: 'Schedule',
    key: 'inspection-schedule',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.InspectionSchedule,
  },

  // Build Listing
  {
    name: STEP_NAMES.UnitListing,
    displayName: 'Details',
    key: 'listing',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.UnitPreferencesPets,
    displayName: 'Pets',
    key: 'pet',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.UnitPreferencesUtilities,
    displayName: 'Utilities',
    key: 'utilities',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.Licenses,
    displayName: 'Licenses',
    key: 'licenses',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.NewJerseyLicenses,
    displayName: 'Licenses',
    key: 'new-jersey-licenses',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.UnitDisclosures,
    displayName: 'Disclosures',
    key: 'disclosures',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.BuildListing,
  },
  {
    name: STEP_NAMES.UserProfile,
    displayName: 'Profile',
    key: 'profile',
    group: getStepGroupByName(SetupDataTypes.User),
    gate: STEP_GATE_NAMES.BuildListing,
  },

  // NextLevelProfile
  {
    name: STEP_NAMES.UnitResidentExperience,
    displayName: 'Resident Experience',
    key: 'resident-experience',
    group: getStepGroupByName(SetupDataTypes.Home),
    gate: STEP_GATE_NAMES.NextLevelProfile,
  },

  // GetPaid
  {
    name: STEP_NAMES.UserIdentity,
    displayName: 'ID Verification',
    key: 'identity',
    group: getStepGroupByName(SetupDataTypes.User),
    gate: STEP_GATE_NAMES.GetPaid,
  },
  {
    name: STEP_NAMES.UserSsn,
    displayName: 'SSN',
    key: 'user-taxes',
    group: getStepGroupByName(SetupDataTypes.UserPayment),
    gate: STEP_GATE_NAMES.GetPaid,
  },
  {
    name: STEP_NAMES.PropertyInsurance,
    displayName: 'Insurance',
    key: 'insurance',
    group: getStepGroupByName(SetupDataTypes.Property),
    gate: STEP_GATE_NAMES.GetPaid,
  },
  {
    name: STEP_NAMES.UserPaymentMethods,
    displayName: 'Get Paid',
    key: 'payment-method',
    group: getStepGroupByName(SetupDataTypes.UserPayment),
    gate: STEP_GATE_NAMES.GetPaid,
  },
];

export function getStepByName(name: any) {
  return STEPS_CONFIGURATION.find((step) => step.name === name);
}

function getStepIndexByName(name: any) {
  return STEPS_CONFIGURATION.findIndex((step) => step.name === name);
}

export function sortStepsByGate(steps: any) {
  return steps?.sort((a: any, b: any) => {
    const firstItem = getStepIndexByName(a.stepName);
    const secondItem = getStepIndexByName(b.stepName);

    return (
      a.unit?.basicInfo.unitNumber - b.unit?.basicInfo.unitNumber ||
      firstItem - secondItem
    );
  });
}

export function getDisplayStatus(status: any) {
  if (status === StepStatus.Created) {
    return STATUS.NOT_TOUCHED;
  }

  if (status === StepStatus.Started || status === StepStatus.Incomplete) {
    return STATUS.INCOMPLETE;
  }

  if (status === StepStatus.Completed) {
    return STATUS.COMPLETED;
  }

  return status;
}

export function getStepPath(step: any, propertyId: any, unitId: any) {
  if (step.stepName === HomeownerSetupStepNames.UnitResidentExperience) {
    return `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${propertyId}/setup-flow/${step.dataRelId}/steps/${step.group.key}/${step.key}/${step.uniqueId}`;
  }

  return `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${propertyId}/setup-flow/${unitId}/steps/${step.group.key}/${step.key}/${step.uniqueId}`;
}

export function getStepPathByName(stepName: any, propertyId: any, unitId: any) {
  const stepConfig = getStepByName(stepName);

  return `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${propertyId}/setup-flow/${unitId}/steps/${stepConfig?.group?.key}/${stepConfig?.key}`;
}

export function isStepCompleted(step: any) {
  return step.status === StepStatus.Completed;
}

export function isStepUnHidden(step: any) {
  return !step.isHidden;
}

export function isCurrentPropertyOrUnitStep(
  step: FlowStepModel,
  property?: HomeownerAccountProperty,
) {
  return (
    step.dataUniqueId === property?.basicInfo.propertyId ||
    property?.units.some(
      (unit: HomeownerAccountUnit) =>
        unit.basicInfo.unitId === step.dataUniqueId,
    )
  );
}

export function getFilteredGateSteps({
  allStepsByGate = {},
  unitId,
  selectedProperty,
  user,
}: {
  allStepsByGate?: Record<string, FlowStepModel[]>;
  // `null` as well as absent: the setup-flow sections hook passes it explicitly,
  // and the filter below only tests it for truthiness.
  unitId?: string | null;
  selectedProperty?: any;
  user?: any;
}) {
  // Both the accumulator's type argument and the map's own type are needed: the
  // `{}` default and the `{}` seed otherwise infer as the empty type, which
  // nothing can be indexed by.
  return Object.keys(allStepsByGate).reduce<Record<string, FlowStepModel[]>>(
    (acc, curr) => {
      const gateStepsFiltered = allStepsByGate[curr]!.filter(
        (step: any) =>
          step.dataRelId === selectedProperty?.basicInfo.propertyId ||
          (unitId &&
          step.stepName !== HomeownerSetupStepNames.UnitResidentExperience
            ? step.group?.name === SetupDataTypes.Home &&
              step.dataRelId === unitId
            : selectedProperty?.units.some(
                (unit: any) => unit.basicInfo.unitId === step.dataRelId,
              )) ||
          step.dataRelId === user?.userId,
      );

      acc[curr] = sortStepsByGate(gateStepsFiltered);

      return acc;
    },
    {},
  );
}
